import { IAppContext } from 'App/AppContext';
import { IGraphInterface } from 'App/AppContextProvider';
import { t, TFunction } from 'i18next';
import Joi from 'joi';
import { ListItem, ListItemVersion } from 'microsoft-graph';
import Entity from 'models/entity';
import { apiUpdateLink } from 'services/Api/linkService';
import { apiUpdateList } from 'services/Api/listService';
import { hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';
import {
  graphGetDocumentLibraryItemThumbnails,
  graphGetDriveItem,
  graphGetDriveItemForUrl,
  graphGetDriveItemPreview,
  graphGetDriveItemUrl,
  graphGetIDriveItemVersions,
  graphGetListItem,
  graphGetListItemVersions,
  graphGetSite,
  graphGetSitePage,
  graphGetSitePageList,
  graphGetSitePageListItem,
  graphPublishDriveItem,
  graphPublishSitePage,
  graphValidateDriveItem,
  graphValidateListItem,
  graphValidateSitePageItem,
} from 'services/Graph/graphService';
import { IDriveItem, IDriveItemVersion, ISite, ISitePage } from 'services/Graph/SharepointInterfaces';
import { getLocalizedMessageOptions } from 'services/Localization/joiValidation';
import logger from 'services/Logging/logService';
import AppError from 'utils/appError';
import { areDifferent } from 'utils/array';
import { ControllerExceptionCodes } from 'utils/controllerException';
import { newGuidNil } from 'utils/guid';
import { sortOnVersion } from 'utils/sorting';
import { apiRequest, GraphScopeId } from './../services/Auth/authConfig';
import Activity from './activity';
import { Approval, ApprovalState } from './approval';
import { Audience } from './audience/audience';
import Group from './group';
import { IOwner } from './owner';
import ResourceLinkChannel from './resourceLinkChannel';
import ResourceList, { ResourceListType } from './resourceList';
import Task from './tasks/task';
import { TeamsChannel } from './teamsChannel';
import { UserReadConfirmation } from './userReadConfirmation';
import { HomeAccount } from './userSetting';

let newLinkId = -1;

//
// Publishing states
//
export enum PublishingStateSharePoint {
  None = 0,
  Draft = 1,
  Published = 2,
}

export enum PublishingStateResourceLink {
  None = 0,
  ToBePublished = 3, // SharePoint indicates major version but there is no publication record
}

//Combined publishing state of SharePoint and Resource Link
export type ResourceLinkPublishingState = PublishingStateSharePoint | PublishingStateResourceLink;

//Create a constant array of the enum values to iterate over. Note that ordering matters here.
export const ResourceLinkPublishingStateOptions: readonly ResourceLinkPublishingState[] = [
  PublishingStateResourceLink.None,
  PublishingStateSharePoint.Draft,
  PublishingStateResourceLink.ToBePublished,
  PublishingStateSharePoint.Published,
];

export enum ResourceLinkCannotPublishReason {
  None = 0,
  IsWholeList = 1,
  ListTypeNotSupported = 2,
  MetadataNotEnabled = 3,
  IsNewLink = 4,
}

//
// Main class
//
export default class ResourceLink implements IOwner {
  linkId: number;

  listId: number;

  ownerId?: string;

  ownerRoleId?: string;

  listItemId?: string;

  driveItemId?: string;

  pageId?: string;

  externalId?: string;

  linkName: string;

  linkURL: string; //don't use to navigate: use getWebURL()

  linkURLFragment: string;

  publicationId?: number;

  pinned: boolean;

  linkTypeId?: number;

  readRequest: boolean;

  usedCount: number;

  entity?: Entity;

  valid?: boolean;

  isValidated: boolean;

  list: ResourceList;

  resourceLinkChannels?: ResourceLinkChannel[];

  userReadConfirmations?: UserReadConfirmation[];

  normIds?: number[];

  kbGroups?: KBTeam[];

  approvals?: Approval[];

  activity?: Activity;

  sourceDriveId?: string;

  //
  // Metadata
  //
  versionInfo: ResourceLinkVersion;

  versions: ResourceLinkVersion[];

  lastPublishedVersion: string | undefined;

  get publishingState(): ResourceLinkPublishingState {
    //
    // Calculate the publishing state based on the last published version and the current version info.
    // - the current version in SharePoint is a major version
    // - the latestPublishedVersion is lower than that, then the state is ToBePublished.
    // - the link can be published
    // Otherwise, use the publishing state from SharePoint.
    //
    // Note: we only return the ToBePublished state when we have both lastPublishedVersion and versionInfo data.
    // This introduces a corner case when the lastPublishedVersion is empty because there is actually none instead of not being loaded.
    // So for views where lastPublishedVersion should be known (retrieved from back-end), call setDefaultLastPublishedVersion() first.
    //
    // Note 2: State ToBePublished is not available for all subscriptions
    // Callers should adjust the state to Published accordingly based on feature availability.
    //
    if (this.lastPublishedVersion && this.versionInfo?.hasData()) {
      const isToBePublished =
        this.canPublish() &&
        ResourceLink.isMajorVersion(this.versionInfo.version) &&
        sortOnVersion(this.lastPublishedVersion, this.versionInfo.version) < 0;

      if (isToBePublished) {
        return PublishingStateResourceLink.ToBePublished;
      } else {
        return this.versionInfo.publishingState || PublishingStateResourceLink.None;
      }
    } else {
      //there is not enough data to calculate the publishing state
      return PublishingStateResourceLink.None;
    }
  }

  setDefaultLastPublishedVersion() {
    if (!this.lastPublishedVersion) {
      this.lastPublishedVersion = '0.0';
    }
  }

  constructor() {
    this.linkId = newLinkId;
    this.linkName = '';
    this.linkURL = '';
    this.linkURLFragment = '';
    this.usedCount = 0;
    this.list = new ResourceList();
    this.listId = 0;
    this.pinned = false;
    this.readRequest = false;
    this.isValidated = false;
    this.versions = [];
    this.versionInfo = new ResourceLinkVersion();
    newLinkId = newLinkId - 1;
  }

  isEqual(item: ResourceLink): boolean {
    //Do not compare data from SharePoint
    //- changed, versions, publishing state, validation
    //Do not compare approvals
    if (item.linkId !== this.linkId) return false;
    if (item.linkName !== this.linkName) return false;
    if (item.linkURL !== this.linkURL) return false;
    if (item.listId !== this.listId) return false;
    if (item.listItemId !== this.listItemId) return false;
    if (item.driveItemId !== this.driveItemId) return false;
    if (item.pageId !== this.pageId) return false;
    if (item.externalId !== this.externalId) return false;
    if (item.pinned !== this.pinned) return false;
    if (item.ownerId !== this.ownerId) return false;
    if (item.ownerRoleId !== this.ownerRoleId) return false;
    if (item.linkTypeId !== this.linkTypeId) return false;

    if (
      areDifferent(item.normIds, this.normIds, (a: number, b: number) => {
        return a === b;
      }) === true
    ) {
      return false;
    }

    return true;
  }

  clone(): ResourceLink {
    const newItem = new ResourceLink();
    newItem.linkId = this.linkId;
    newItem.linkName = this.linkName;
    newItem.linkURL = this.linkURL;
    newItem.usedCount = this.usedCount;
    newItem.listId = this.listId;
    newItem.listItemId = this.listItemId;
    newItem.driveItemId = this.driveItemId;
    newItem.list = this.list;
    newItem.pageId = this.pageId;
    newItem.externalId = this.externalId;
    newItem.pinned = this.pinned;
    newItem.valid = this.valid;
    newItem.isValidated = this.isValidated;
    newItem.approvals = this.approvals ? [...this.approvals] : undefined;
    newItem.versionInfo = this.versionInfo.clone();
    newItem.ownerId = this.ownerId;
    newItem.versions = [...this.versions];
    newItem.normIds = this.normIds ? [...this.normIds] : undefined;
    newItem.ownerRoleId = this.ownerRoleId;
    newItem.readRequest = this.readRequest;
    newItem.userReadConfirmations = this.userReadConfirmations ? [...this.userReadConfirmations] : undefined;
    newItem.linkTypeId = this.linkTypeId;
    newItem.sourceDriveId = this.sourceDriveId;

    return newItem;
  }

  isWholeList(): boolean {
    if (!this.driveItemId && !this.listItemId && !this.pageId && this.linkURL === 'list') return true;

    return false;
  }

  setWholeList(list: ResourceList): boolean {
    this.list = list;
    this.listId = list.listId;
    this.linkName = list.name;
    this.linkURL = 'list';
    this.driveItemId = undefined;
    this.listItemId = undefined;
    this.pageId = undefined;

    return false;
  }

  validate(localizedFields: Record<string, string>): Joi.ValidationResult {
    if (this.isWholeList()) {
      //return a dummy validation result
      const schema: Joi.ObjectSchema = Joi.object({
        name: Joi.string().min(4).max(4),
      });

      return schema.validate({ url: this.linkURL }, { abortEarly: false });
    }

    const schema: Joi.ObjectSchema = Joi.object({
      name: Joi.string().min(1).max(512).required().label(localizedFields['name'] || 'name'),
      url: Joi.string().min(1).max(800).required().uri().label(localizedFields['url'] || 'url'),
      externalId: Joi.string().allow('').max(256).label(localizedFields['externalId'] || 'externalId'),
    }).prefs(getLocalizedMessageOptions());

    return schema.validate(
      { name: this.linkName.trim(), url: this.linkURL.trim(), externalId: this.externalId?.trim() ?? '' },
      { abortEarly: false },
    );
  }

  async getThumbnail(appContext: IAppContext): Promise<string> {
    if (!this.list.spDriveId || !this.driveItemId) return '';

    try {
      const graphInterface = await appContext.getGraphInterface(GraphScopeId.Library, this.list.altTenantId);
      const thumbUrl = await graphGetDocumentLibraryItemThumbnails(
        graphInterface.client,
        this.list.spDriveId,
        this.driveItemId,
      );

      return thumbUrl ?? '';
    } catch (err) {
      logger.debug('Error while getting the thumbnail url', err);

      return '';
    }
  }

  async publish(appContext: IAppContext): Promise<void> {
    try {
      switch (this.list.listType) {
        case ResourceListType.SitePageLibrary:
          if (!this.list.spSiteId || !this.pageId) {
            throw new AppError('spSiteId or pageId is empty');
          }
          const graphInterfacePage = await appContext.getGraphInterface(GraphScopeId.Library, this.list.altTenantId);
          await graphPublishSitePage(graphInterfacePage.client, this.list.spSiteId, this.pageId);
          this.setMetaData(graphInterfacePage, undefined, appContext);
          break;
        case ResourceListType.DocumentLibrary:
          if (!this.list.spDriveId || !this.driveItemId) {
            throw new AppError('spDriveId or driveItemId is empty');
          }
          const graphInterfaceDoc = await appContext.getGraphInterface(GraphScopeId.Library, this.list.altTenantId);
          await graphPublishDriveItem(graphInterfaceDoc.client, this.list.spDriveId, this.driveItemId);
          this.setMetaData(undefined, graphInterfaceDoc, appContext);
          break;
        default:
          throw new AppError('List type not supported for publishing');
      }
    } catch (err) {
      logger.debug('publish', err);

      const appErr = err as AppError;
      if (appErr.code === 'notAllowed') {
        appContext.showNotification(t('sharepoint:Errors.ResourceLocked'), true);
      } else {
        appContext.showNotification(t('sharepoint:Errors.AccessDenied'), true);
      }

      return;
    }
  }

  canPublish(): boolean {
    return this.cannotPublishReason() === ResourceLinkCannotPublishReason.None;
  }

  cannotPublishReason(): ResourceLinkCannotPublishReason {
    //note that content in virtual lists cannot actually be published
    //but we allow publishing for links in virtual lists for better user experience in trials
    //the back-end will skip any publish requests for virtual lists
    if (this.linkId < 0) return ResourceLinkCannotPublishReason.IsNewLink;
    if (this.isWholeList()) return ResourceLinkCannotPublishReason.IsWholeList;
    if (this.list.listType === ResourceListType.CustomList) return ResourceLinkCannotPublishReason.ListTypeNotSupported;
    if (this.list.listType === ResourceListType.WebURL) return ResourceLinkCannotPublishReason.ListTypeNotSupported;
    if (this.list.enableMetaData !== true) return ResourceLinkCannotPublishReason.MetadataNotEnabled;

    return ResourceLinkCannotPublishReason.None;
  }

  getMetaDataBatchRequest(): string | undefined {
    if (this.isWholeList()) {
      return undefined;
    }

    let request: string | undefined = undefined;
    if (this.list && this.list.enableMetaData === true && !this.list.isVirtual) {
      switch (this.list.listType) {
        case ResourceListType.DocumentLibrary:
          if (this.driveItemId && this.list.spDriveId) {
            request = `/drives/${this.list.spDriveId}/items/${this.driveItemId}/versions`;
          }
          break;
        case ResourceListType.SitePageLibrary:
          if (this.list.spSiteId && this.list.spListId && this.listItemId) {
            request = `sites/${this.list.spSiteId}/lists/${this.list.spListId}/items/${this.listItemId}/versions`;
          }
          break;
      }
    }

    return request;
  }

  applyMetaDataBatchResultListItem(versions: ListItemVersion[]) {
    if (!versions) return;
    this.versions = [];

    for (let idx = 0; idx < versions.length; idx++) {
      const version = versions[idx];
      if (version.id) {
        const newVersion = new ResourceLinkVersion();
        newVersion.id = version.id;
        newVersion.version = version.id;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const fields: any = version.fields;
        if (fields && fields['Modified']) {
          newVersion.date = new Date(fields['Modified']);
        }
        this.versions.push(newVersion);
      }
    }

    if (this.versions.length > 0) {
      const latestVersion = this.versions[0];
      this.versionInfo = latestVersion;
    }
  }

  applyMetaDataBatchResultDriveItem(versions: IDriveItemVersion[]) {
    if (!versions) return;
    this.versions = [];

    for (let idx = 0; idx < versions.length; idx++) {
      const version = versions[idx];
      if (version.id) {
        const newVersion = new ResourceLinkVersion();
        newVersion.id = version.id;
        newVersion.date = version?.lastModifiedDateTime ? new Date(version?.lastModifiedDateTime) : new Date();
        newVersion.version = version.id;
        this.versions.push(newVersion);
      }
    }

    if (this.versions.length > 0) {
      const latestVersion = this.versions[0];
      this.versionInfo.date = latestVersion.date;
      this.versionInfo.version = latestVersion.id;
      this.versionInfo.publishingState = ResourceLink.isMajorVersion(this.versionInfo.version)
        ? PublishingStateSharePoint.Published
        : PublishingStateSharePoint.Draft;
    }
  }

  setMetaDataForDriveItem(item: IDriveItem) {
    if (item.publication) {
      this.versionInfo.version = item.publication.versionId ?? '';
      this.versionInfo.publishingState = ResourceLink.isMajorVersion(this.versionInfo.version)
        ? PublishingStateSharePoint.Published
        : PublishingStateSharePoint.Draft;
    }
    this.versionInfo.date = item?.lastModifiedDateTime ? new Date(item.lastModifiedDateTime) : undefined;
  }

  setMetaDataForPage(page: ISitePage) {
    if (page?.publishingState?.versionId) {
      this.versionInfo.id = page?.publishingState.versionId;
      this.versionInfo.date = page?.lastModifiedDateTime ? new Date(page?.lastModifiedDateTime) : new Date();
      this.versionInfo.version = page?.publishingState?.versionId;
      this.versionInfo.publishingState = ResourceLink.getPublishingStateFromSitePage(page?.publishingState?.level);
    }
  }

  static isMajorVersion(version: string | undefined): boolean {
    return version?.endsWith('.0') ?? false;
  }

  async setMetaData(
    graphInterfacePages: IGraphInterface | undefined,
    graphInterfaceDocuments: IGraphInterface | undefined,
    appContext: IAppContext,
  ): Promise<void> {
    //
    // Get all versions and last modification date of the item
    //
    try {
      if (this.isWholeList()) {
        return;
      }

      if (this.list && this.list.enableMetaData === true && !this.list.isVirtual) {
        switch (this.list.listType) {
          case ResourceListType.DocumentLibrary:
            if (!graphInterfaceDocuments) return;
            if (!this.list.spDriveId || !this.driveItemId) {
              logger.debug('Error while getting the meta data: drive id or drive item id is empty', this.list);

              return;
            }

            const versions = await graphGetIDriveItemVersions(
              graphInterfaceDocuments.client,
              this.list.spDriveId,
              this.driveItemId,
            );

            this.applyMetaDataBatchResultDriveItem(versions);
            break;
          case ResourceListType.SitePageLibrary:
            if (!graphInterfacePages) return;
            if (!this.list.spSiteId || !this.pageId) {
              logger.debug('Error while getting the meta data: site id or page id is empty', this.list);

              return;
            }

            //try to fix spListId
            if (!this.list.spListId) {
              const pageList = await graphGetSitePageList(graphInterfacePages.client, this.list.spSiteId);
              this.list.spListId = pageList?.id;
              if (this.list.spListId && hasUserFeatureGenericManager(appContext)) {
                const accessToken = await appContext.getAccessToken(apiRequest.scopes);
                await apiUpdateList(accessToken, this.list);
                logger.debug('spListId updated', this.list);
              }
            }

            //try to fix listItemId
            if (!this.listItemId && this.list.spListId) {
              const listItem = await graphGetSitePageListItem(
                graphInterfacePages.client,
                this.list.spSiteId,
                this.list.spListId,
                this.linkURL,
              );
              if (listItem?.id) {
                this.listItemId = listItem.id;
                const accessToken = await appContext.getAccessToken(apiRequest.scopes);
                await apiUpdateLink(this, false, accessToken, appContext.globalDataCache);
                logger.debug('listItemId updated', this);
              }
            }

            if (this.listItemId && this.list.spListId) {
              //get the complete version history
              const versions = await graphGetListItemVersions(
                graphInterfacePages.client,
                this.list.spSiteId,
                this.list.spListId,
                this.listItemId,
              );

              this.applyMetaDataBatchResultListItem(versions);
            }

            //there should be at least 1 version
            //when this fails, try to get the latest version from the page
            if (!this.versions || this.versions.length === 0) {
              //we cannot get the list item. fill the version history, only from the page model
              const page = await graphGetSitePage(graphInterfacePages.client, this.list.spSiteId, this.pageId, false);
              this.versionInfo.publishingState = ResourceLink.getPublishingStateFromSitePage(
                page?.publishingState.level,
              );
              if (page?.publishingState?.versionId) {
                this.versionInfo.id = page.publishingState.versionId;
                this.versionInfo.date = page.lastModifiedDateTime ? new Date(page.lastModifiedDateTime) : new Date();
                this.versionInfo.version = page.publishingState.versionId;
              }
            }
            break;
          default:
            break;
        }
      }
    } catch (err) {
      logger.debug('Error while getting the meta data', err);
    }
  }

  async getEmbedURL(appContext: IAppContext): Promise<string | undefined> {
    logger.debug('getEmbedURL', this);

    if (this.isWholeList()) {
      //return the web URL of the list
      if (this.list) {
        return this.list.webURL || '';
      }
    }

    let url: string | undefined = undefined;

    try {
      if (this.list) {
        switch (this.list.listType) {
          case ResourceListType.DocumentLibrary:
            if (this.driveItemId && this.list.spDriveId) {
              const graphInterface = await appContext.getGraphInterface(GraphScopeId.Library, this.list.altTenantId);

              let driveId: string = this.list.spDriveId;
              let driveItemId: string = this.driveItemId;

              const result = await graphGetDriveItemUrl(appContext, driveId, driveItemId, this.linkURL);
              if (result?.isUrlFile && result.url) {
                if (result.urlClassification?.embedable) {
                  const item = await graphGetDriveItemForUrl(graphInterface.client, result.url);
                  driveId = item?.parentReference?.driveId ?? '';
                  driveItemId = item?.id ?? '';
                } else {
                  driveId = '';
                  driveItemId = '';
                }
              }

              if (driveId && driveItemId) {
                const embedUrl = await graphGetDriveItemPreview(graphInterface.client, driveId, driveItemId);

                if (embedUrl) {
                  url = embedUrl;
                }
              }
            }
            break;
          default:
            break;
        }
      }
    } catch (err) {
      logger.debug('Error while getting the embed url', err);
    } finally {
      if (url && this.linkURLFragment && this.linkURLFragment.trim() !== '') {
        url += this.linkURLFragment;
      }
    }

    return url;
  }

  async getWebURL(appContext: IAppContext): Promise<string> {
    //
    // Get the current web URL of the link from SharePoint
    // - when available, also update the versionInfo
    // - support for .url files
    //
    logger.debug('getWebURL', this);

    if (this.isWholeList() && this.list) {
      //return the web URL of the list
      return this.list.webURL || '';
    }

    let url: string = this.linkURL;
    let spUrl: string = '';
    let dontUpdateLinkUrl = false;

    try {
      if (this.list) {
        switch (this.list.listType) {
          case ResourceListType.DocumentLibrary:
            if (this.driveItemId && this.list.spDriveId) {
              const graphInterface = await appContext.getGraphInterface(GraphScopeId.Library, this.list.altTenantId);
              const item = await graphGetDriveItem(graphInterface.client, this.list.spDriveId, this.driveItemId);
              if (item && item.webUrl) {
                //try to resolve .url files
                const result = await graphGetDriveItemUrl(
                  appContext,
                  this.list.spDriveId,
                  this.driveItemId,
                  item.webUrl,
                );
                if (result?.url) {
                  spUrl = result.url;
                  if (result.isUrlFile) {
                    //don't update the link URL with a .url file
                    dontUpdateLinkUrl = true;
                  }
                }
                //update metadata
                this.setMetaDataForDriveItem(item);
              }
            }
            break;
          case ResourceListType.CustomList:
            if (this.listItemId && this.list.spSiteId && this.list.spListId) {
              const graphInterface = await appContext.getGraphInterface(GraphScopeId.Pages, this.list.altTenantId);
              const item = await graphGetListItem(
                graphInterface.client,
                this.list.spSiteId,
                this.list.spListId,
                this.listItemId,
              );
              const listItemUrl = this.buildLinkURLFromListItem(item);
              if (listItemUrl) {
                spUrl = listItemUrl;
              }
              //no metadata to update
            }
            break;
          case ResourceListType.SitePageLibrary:
            if (this.pageId && this.list.spSiteId) {
              const graphInterface = await appContext.getGraphInterface(GraphScopeId.Pages, this.list.altTenantId);

              const page: ISitePage = await graphGetSitePage(
                graphInterface.client,
                this.list.spSiteId,
                this.pageId,
                false,
              );

              spUrl = page.webUrl;
              if (!spUrl.startsWith('https://')) {
                const site: ISite = await graphGetSite(graphInterface.client, this.list.spSiteId);
                spUrl = site.webUrl + '/' + url;
              }

              //update metadata
              this.setMetaDataForPage(page);
            }
            break;
          case ResourceListType.WebURL:
          //nothing to-do
        }
      }
    } catch (err) {
      logger.debug('Error while getting the web url', err);
    } finally {
      if (!dontUpdateLinkUrl && spUrl && spUrl !== url && this.list && this.list.listType !== ResourceListType.WebURL) {
        try {
          //update the link with the new URL from SharePoint
          this.linkURL = spUrl;
          const accessToken = await appContext.getAccessToken(apiRequest.scopes);
          await apiUpdateLink(this, true, accessToken, appContext.globalDataCache);
        } catch (err) {
          logger.debug('Error update link after getting web url', err);
        }
      }
      if (spUrl) {
        url = spUrl;
      }
      if (this.linkURLFragment && this.linkURLFragment.trim() !== '') {
        url += this.linkURLFragment;
      }
    }

    return url;
  }

  buildLinkURLFromListItem = (item: ListItem | undefined): string | undefined => {
    if (item && item.webUrl) {
      let url = item.webUrl.substring(0, item.webUrl.lastIndexOf('/'));
      url = url + '/DispForm.aspx?';
      url = url + 'ID=';
      url = url + item.id?.toString();

      return url;
    }

    return undefined;
  };

  async isValid(appContext: IAppContext): Promise<boolean | undefined> {
    this.isValidated = true;
    if (this.isWholeList()) return true;
    if (!this.list) return false;
    if (this.list.isVirtual) return true;

    if (this.list.altTenantId) {
      //don't validate links in other tenants except the home account of the current user
      const currentHomeAccount = appContext.globalDataCache.userSettings.get(HomeAccount) as string;
      if (!currentHomeAccount || this.list.altTenantId !== currentHomeAccount) {
        return undefined;
      }
    }

    switch (this.list.listType) {
      case ResourceListType.DocumentLibrary:
        if (this.driveItemId && this.list.spDriveId) {
          const graphInterface = await appContext.getGraphInterface(GraphScopeId.Library, this.list.altTenantId);

          return graphValidateDriveItem(graphInterface.client, this.list.spDriveId, this.driveItemId);
        } else {
          return false;
        }

      case ResourceListType.CustomList:
        if (this.listItemId && this.list.spSiteId && this.list.spListId) {
          const graphInterface = await appContext.getGraphInterface(GraphScopeId.Pages, this.list.altTenantId);

          return graphValidateListItem(graphInterface.client, this.list.spSiteId, this.list.spListId, this.listItemId);
        } else {
          return false;
        }

      case ResourceListType.SitePageLibrary:
        if (this.pageId && this.list.spSiteId) {
          const graphInterface = await appContext.getGraphInterface(GraphScopeId.Pages, this.list.altTenantId);

          return graphValidateSitePageItem(graphInterface.client, this.list.spSiteId, this.pageId);
        } else {
          return false;
        }

      case ResourceListType.WebURL:
        return fetch(this.linkURL, { mode: 'no-cors' })
          .then((r) => {
            return true;
          })
          .catch((e) => {
            return false;
          });

      default:
        return undefined;
    }
  }

  static getPublishingStateFromSitePage = (state: string | undefined | null): PublishingStateSharePoint => {
    switch (state) {
      case 'published':
        return PublishingStateSharePoint.Published;
      case 'checkout':
      case 'draft':
        return PublishingStateSharePoint.Draft;
      default:
        return PublishingStateSharePoint.None;
    }
  };

  static getPublicationStateText(publishingState: ResourceLinkPublishingState | undefined, t: TFunction): string {
    //the states are hard coded by Microsoft Graph API
    switch (publishingState) {
      case PublishingStateSharePoint.Published:
        return t('sharepoint:PagePublicationState.Published');
      case PublishingStateResourceLink.ToBePublished:
        return t('sharepoint:PagePublicationState.ToBePublished');
      case PublishingStateSharePoint.Draft:
        return t('sharepoint:PagePublicationState.Draft');
      default:
        return '';
    }
  }

  getCannotPublishReasonText(t: TFunction): string {
    const reason = this.cannotPublishReason();
    const category = `${this.list.name} (${this.list.getListTypeText(t)})`;

    switch (reason) {
      case ResourceLinkCannotPublishReason.IsWholeList:
        return t('sharepoint:CannotPublishReason.IsWholeList', { category: category });
      case ResourceLinkCannotPublishReason.ListTypeNotSupported:
        return t('sharepoint:CannotPublishReason.ListTypeNotSupported', { category: category });
      case ResourceLinkCannotPublishReason.MetadataNotEnabled:
        return t('sharepoint:CannotPublishReason.MetadataNotEnabled', { category: category });
      case ResourceLinkCannotPublishReason.IsNewLink:
        return t('sharepoint:CannotPublishReason.IsNewLink', { category: category });
      default:
        return '';
    }
  }

  // Return true if there is at least one read confirmation
  getRC(): UserReadConfirmation | undefined {
    return this.userReadConfirmations && this.userReadConfirmations[0];
  }

  // Set a single read confirmation
  setRC(usr: UserReadConfirmation): void {
    this.userReadConfirmations = [usr];
  }

  // Return true if there is at least one read confirmation
  hasRC(): boolean {
    return (this.userReadConfirmations && this.userReadConfirmations.length > 0) ?? false;
  }

  // Return true if there is at least one unread read confirmation
  hasRCUnread(): boolean {
    return (
      (this.userReadConfirmations &&
        this.userReadConfirmations.length > 0 &&
        this.userReadConfirmations[0].readConfirmed === undefined) ??
      false
    );
  }

  // Return true if there is at least one read confirmation marked as read
  hasRCRead(): boolean {
    return (
      (this.userReadConfirmations &&
        this.userReadConfirmations.length > 0 &&
        this.userReadConfirmations[0].readConfirmed !== undefined) ??
      false
    );
  }
}

//
// Helper classes
//
export class ResourceLinkVersion {
  id: string;

  date?: Date;

  version: string;

  publishingState?: PublishingStateSharePoint;

  constructor() {
    this.id = '';
    this.version = '';
  }

  hasData(): boolean {
    return this.version !== '';
  }

  clone(): ResourceLinkVersion {
    const newVersion = new ResourceLinkVersion();
    newVersion.id = this.id;
    newVersion.date = this.date;
    newVersion.version = this.version;
    newVersion.publishingState = this.publishingState;

    return newVersion;
  }
}

export interface ILinkRow {
  item: ResourceLink;
  isBusy: boolean;
  key?: string; //optional composite key for the row
  audience?: Audience; //optional audience id if the row is shown in an audience context
}

export class EntityLink extends Entity {
  pinned: boolean;

  linkURLFragment?: string;

  constructor() {
    super();
    this.pinned = false;
  }
}

export class KBTeam {
  group: Group;

  channels: TeamsChannel[];

  links: ResourceLink[];

  hash: string;

  constructor() {
    this.group = new Group(newGuidNil());
    this.channels = [];
    this.links = [];
    this.hash = newGuidNil();
  }

  setHash() {
    this.hash = this.group.id + '#' + this.channels.map((c) => c.channelId ?? newGuidNil()).join(',');
  }
}

export enum PublicationResultStatus {
  None = 0,
  Success = 1,
  Failed = 9,
}

export class PublicationResult {
  publicationId: number;

  created?: Date;

  createdById?: string;

  linkId: number;

  link?: ResourceLink;

  status?: PublicationResultStatus;

  result?: string;

  oldVersion?: string;

  newVersion?: string;

  lastModified?: Date;

  readConfirmationsSend?: number;

  smallChange?: boolean;

  approvalCompleted?: Date;

  approvalState?: ApprovalState;

  approvers?: string[];

  approvalTask?: Task;

  constructor() {
    this.publicationId = 0;
    this.linkId = 0;
  }

  getPublishResultStatusText(t: TFunction): string {
    if (this.status === PublicationResultStatus.Success) {
      return t('sharepoint:PublicationStatus.Success');
    }

    switch (this.result) {
      case ControllerExceptionCodes.Code_General_NotAuthorized:
        return t('sharepoint:PublicationStatus.NoPermissions');
      case ControllerExceptionCodes.Code_Graph_FileLocked:
        return t('sharepoint:PublicationStatus.Locked');
      default:
        return t('sharepoint:PublicationStatus.Failed');
    }
  }
}
